/**
 * Intel Packet Component
 * 
 * Displays collected intelligence from Overwatch articles saved to device notes.
 * Professional intelligence log format with classification-style presentation.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Button,
  alpha,
  TextField,
  InputAdornment,
  Collapse,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Select,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  LocationOn as LocationIcon,
  OpenInNew as OpenInNewIcon,
  Download as ExportIcon,
  Clear as ClearIcon,
  Article as ArticleIcon,
  Security as SecurityIcon,
  GpsFixed as TargetIcon,
  InsertDriveFile as FileIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  AutoFixHigh as AIInsightsIcon,
  TravelExplore as EnrichIcon,
  Edit as EditIcon,
  MyLocation as ZoomToIcon,
  DeleteSweep as DeleteAllIcon,
  NoteAdd as AddNoteIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import pathfinderApi, { EntityNote, NoteAttachment } from '../../services/pathfinderApi';
import apiService, { EnrichmentResponse } from '../../services/api';

interface IntelPacketProps {
  /** Full panel mode */
  isFullPanel?: boolean;
  /** Device IDs currently loaded on the map */
  loadedDeviceIds?: string[];
  /** Callback to zoom to a location on the map */
  onZoomToLocation?: (lat: number, lng: number, zoom?: number) => void;
  /** Current map bounds for filtering entries by visible area */
  mapBounds?: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
}

interface ParsedIntel {
  id: number;
  deviceId: string;
  deviceLabel: string;
  watchlistId: number;
  title: string;
  date: string;
  category: string;
  sentiment?: string;
  location?: {
    lat: number;
    lng: number;
    name?: string;
  };
  summary: string;
  sourceLink?: string;
  addedAt: string;
  rawContent: string;
  type: 'overwatch' | 'pol' | 'user'; // Type of note
  createdBy?: string; // Who created the note
}

interface WatchlistEntry {
  id: number;
  registration_id: string;
  label?: string;
  notes?: string;
}

const IntelPacket: React.FC<IntelPacketProps> = ({
  isFullPanel: _isFullPanel = false,
  loadedDeviceIds = [],
  onZoomToLocation,
  mapBounds,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  // Intelligence-themed colors
  const colors = useMemo(() => ({
    // Primary intel colors
    primary: '#3b82f6',
    accent: '#f59e0b',
    text: isDark ? '#e2e8f0' : '#1e293b',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    textMuted: isDark ? '#64748b' : '#94a3b8',
    // Background layers
    bg: isDark ? '#0f172a' : '#f8fafc',
    surface: isDark ? '#1e293b' : '#ffffff',
    surfaceHover: isDark ? '#334155' : '#f1f5f9',
    // Borders
    border: isDark ? '#334155' : '#e2e8f0',
    borderAccent: isDark ? '#475569' : '#cbd5e1',
    // Classification colors
    overwatch: '#f59e0b',
    pol: '#10b981', // Green for Pattern of Life
    userNote: '#3b82f6',
    // Sentiment/Priority
    positive: '#22c55e',
    negative: '#ef4444',
    neutral: '#6b7280',
    // Categories
    critical: '#dc2626',
    high: '#f97316',
    medium: '#eab308',
    low: '#22c55e',
    // Success/enriched
    success: '#10b981',
  }), [isDark]);

  // State
  const [loading, setLoading] = useState(true);
  const [intelItems, setIntelItems] = useState<ParsedIntel[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedDevice, setSelectedDevice] = useState<string>('');
  const [filterByLoadedDevices, setFilterByLoadedDevices] = useState(true); // Default to filtering by loaded devices
  const [filterByMapExtent, setFilterByMapExtent] = useState(false); // Filter by visible map area
  const [selectedType, setSelectedType] = useState<'all' | 'pol' | 'overwatch' | 'user'>('all');
  const [showStats, setShowStats] = useState(true);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);
  
  // Attachment state
  const [attachments, setAttachments] = useState<Map<number, NoteAttachment[]>>(new Map());
  const [uploadingNoteId, setUploadingNoteId] = useState<number | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [processingAttachmentId, setProcessingAttachmentId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentUploadNoteId = useRef<number | null>(null);
  
  // Enrichment state
  const [enrichmentResults, setEnrichmentResults] = useState<Map<number, EnrichmentResponse>>(new Map());
  const [enrichmentEnabled, setEnrichmentEnabled] = useState<boolean | null>(null);
  const [updatingTitleId, setUpdatingTitleId] = useState<number | null>(null);
  
  // Enrichment chat state
  const [enrichChatOpen, setEnrichChatOpen] = useState<ParsedIntel | null>(null);
  const [enrichChatQuery, setEnrichChatQuery] = useState('');
  const [enrichChatLoading, setEnrichChatLoading] = useState(false);
  const [enrichChatHistory, setEnrichChatHistory] = useState<Map<number, Array<{
    type: 'user' | 'assistant';
    content: string;
    sources?: Array<{ title: string; url: string; snippet: string }>;
  }>>>(new Map());
  
  // Edit note state
  const [editingNote, setEditingNote] = useState<ParsedIntel | null>(null);
  const [editedContent, setEditedContent] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteAllConfirmOpen, setDeleteAllConfirmOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  
  // Add note state
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [newNoteDeviceId, setNewNoteDeviceId] = useState<number | ''>('');
  const [savingNewNote, setSavingNewNote] = useState(false);
  const [newNoteFile, setNewNoteFile] = useState<File | null>(null);
  const newNoteFileInputRef = useRef<HTMLInputElement>(null);

  // Parse note content - handles both Overwatch intel and user notes
  const parseNote = useCallback((note: EntityNote, device: WatchlistEntry): ParsedIntel => {
    const content = note.content;
    
    // Check if this is an Overwatch intel note or POL (Pattern of Life) intel
    const isOverwatchIntel = content.includes('[OVERWATCH INTEL]') || content.includes('OVERWATCH INTEL');
    const isPOLIntel = content.includes('[POL INTEL]');

    if (isOverwatchIntel || isPOLIntel) {
      try {
        // Extract title (between ** markers)
        const titleMatch = content.match(/\*\*(.+?)\*\*/);
        const title = titleMatch ? titleMatch[1] : 'Untitled';

        // Extract date (support both formats)
        const dateMatch = content.match(/(?:📅 )?Date(?:\sRange)?:\s*(.+)/);
        const date = dateMatch ? dateMatch[1].trim() : '';

        // Extract category (support both formats)
        const categoryMatch = content.match(/(?:📂 )?Category: (.+)/);
        const category = categoryMatch ? categoryMatch[1].trim() : 'Uncategorized';

        // Extract sentiment (support both formats) - POL intel may have confidence instead
        const sentimentMatch = content.match(/(?:📊 )?Sentiment: (.+)/);
        const confidenceMatch = content.match(/(?:🎯 )?Confidence: (.+)/);
        const sentiment = sentimentMatch ? sentimentMatch[1].trim() : 
                         (confidenceMatch ? `Confidence: ${confidenceMatch[1].trim()}` : undefined);

        // Extract location (support multiple formats)
        let location: ParsedIntel['location'] | undefined;
        // Format 1: Location: lat, lng (name)
        const locationMatch = content.match(/(?:📍 )?Location: ([-\d.]+), ([-\d.]+)(?:\s*\((.+?)\))?/);
        if (locationMatch) {
          location = {
            lat: parseFloat(locationMatch[1]),
            lng: parseFloat(locationMatch[2]),
            name: locationMatch[3] || undefined,
          };
        }
        
        // Format 2: Coordinates: lat, lng (used in repeated visit notes when Location has place name)
        if (!location) {
          const coordsMatch = content.match(/Coordinates: ([-\d.]+), ([-\d.]+)/);
          if (coordsMatch) {
            // Also try to extract the place name from "Location:" line
            const placeNameMatch = content.match(/Location: ([^\d\n][^\n]*?)(?:\n|$)/);
            location = {
              lat: parseFloat(coordsMatch[1]),
              lng: parseFloat(coordsMatch[2]),
              name: placeNameMatch ? placeNameMatch[1].trim() : undefined,
            };
          }
        }

        // Extract summary (support both formats - look for Summary: section)
        const summaryMatch = content.match(/(?:📝 )?Summary:\n([\s\S]+?)(?:\n\n(?:🔗 |🔝 |📋 )?(?:Source:|Frequent|Visit)|={10,}|━━━)/);
        const summary = summaryMatch ? summaryMatch[1].trim() : '';

        // Extract source link (support both formats)
        const linkMatch = content.match(/(?:🔗 )?Source: (https?:\/\/.+)/);
        const sourceLink = linkMatch ? linkMatch[1].trim() : undefined;

        // Extract added date (support both Overwatch and POL formats)
        const addedMatch = content.match(/(?:Added via (?:Overwatch Intel|Pattern of Life Analysis) on|Auto-generated via Pattern of Life Analysis on) (.+)/);
        const addedAt = addedMatch ? addedMatch[1].trim() : note.created_at;

        // Determine type based on content
        const isPolIntel = content.includes('[POL INTEL]');
        
        return {
          id: note.id,
          deviceId: device.registration_id,
          deviceLabel: device.label || device.registration_id,
          watchlistId: device.id,
          title,
          date,
          category,
          sentiment,
          location,
          summary,
          sourceLink,
          addedAt,
          rawContent: content,
          type: isPolIntel ? 'pol' : 'overwatch',
          createdBy: note.created_by,
        };
      } catch (error) {
        console.error('Failed to parse intel note:', error);
        // Fall through to user note handling
      }
    }

    // Parse as user note
    const lines = content.split('\n');
    const firstLine = lines[0] || '';
    const title = firstLine.length > 50 ? firstLine.substring(0, 50) + '...' : firstLine || 'User Note';
    
    return {
      id: note.id,
      deviceId: device.registration_id,
      deviceLabel: device.label || device.registration_id,
      watchlistId: device.id,
      title,
      date: new Date(note.created_at).toLocaleDateString('en-US', { 
        year: 'numeric', month: 'long', day: 'numeric' 
      }),
      category: 'User Note',
      sentiment: undefined,
      location: undefined,
      summary: content,
      sourceLink: undefined,
      addedAt: note.created_at,
      rawContent: content,
      type: 'user',
      createdBy: note.created_by,
    };
  }, []);

  // Load all intel from watchlist devices
  const loadIntel = useCallback(async () => {
    setLoading(true);
    try {
      // Use optimized endpoint that gets watchlist + notes in single query
      const watchlistWithNotes = await pathfinderApi.getWatchlistWithNotes();
      
      // Parse all notes from all devices
      const allIntel: ParsedIntel[] = [];
      
      watchlistWithNotes.forEach((device) => {
        const watchlistEntry: WatchlistEntry = {
          id: device.id,
          registration_id: device.registration_id,
          label: device.label,
          notes: device.notes,
        };
        
        device.entity_notes.forEach(note => {
          const parsed = parseNote(note, watchlistEntry);
          allIntel.push(parsed);
        });
      });

      // Sort by added date (newest first)
      allIntel.sort((a, b) => 
        new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime()
      );

      setIntelItems(allIntel);
    } catch (error) {
      console.error('Failed to load intel packet:', error);
    } finally {
      setLoading(false);
    }
  }, [parseNote]);

  // Initial load
  useEffect(() => {
    loadIntel();
  }, [loadIntel]);

  // Listen for refresh events (e.g., when auto-intel is generated)
  useEffect(() => {
    const handleRefresh = () => {
      loadIntel();
    };
    window.addEventListener('refresh-intel-packet', handleRefresh);
    window.addEventListener('refresh-watchlist', handleRefresh);
    return () => {
      window.removeEventListener('refresh-intel-packet', handleRefresh);
      window.removeEventListener('refresh-watchlist', handleRefresh);
    };
  }, [loadIntel]);

  // Load attachments for a note when expanded
  const loadAttachments = useCallback(async (noteId: number) => {
    if (attachments.has(noteId)) return; // Already loaded
    
    try {
      const noteAttachments = await pathfinderApi.getNoteAttachments(noteId);
      setAttachments(prev => new Map(prev).set(noteId, noteAttachments));
    } catch (error) {
      console.error('Failed to load attachments for note:', noteId, error);
    }
  }, [attachments]);

  // Handle file upload
  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const noteId = currentUploadNoteId.current;
    
    if (!file || !noteId) return;
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    setUploadingNoteId(noteId);
    setUploadProgress(0);
    
    try {
      // Simulate progress (actual progress would need axios interceptor)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 100);
      
      const attachment = await pathfinderApi.uploadNoteAttachment(noteId, file);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Add to attachments map
      setAttachments(prev => {
        const noteAttachments = prev.get(noteId) || [];
        return new Map(prev).set(noteId, [...noteAttachments, attachment]);
      });
      
      setTimeout(() => {
        setUploadingNoteId(null);
        setUploadProgress(0);
      }, 500);
      
    } catch (error) {
      console.error('Failed to upload attachment:', error);
      setUploadingNoteId(null);
      setUploadProgress(0);
    }
  }, []);

  // Trigger file upload dialog
  const triggerUpload = useCallback((noteId: number) => {
    currentUploadNoteId.current = noteId;
    fileInputRef.current?.click();
  }, []);

  // Delete attachment
  const handleDeleteAttachment = useCallback(async (attachmentId: number, noteId: number) => {
    try {
      await pathfinderApi.deleteNoteAttachment(attachmentId);
      setAttachments(prev => {
        const noteAttachments = prev.get(noteId) || [];
        return new Map(prev).set(noteId, noteAttachments.filter(a => a.id !== attachmentId));
      });
    } catch (error) {
      console.error('Failed to delete attachment:', error);
    }
  }, []);

  // Process attachment with AI and append insights
  const handleProcessAttachment = useCallback(async (attachmentId: number) => {
    setProcessingAttachmentId(attachmentId);
    
    try {
      const result = await pathfinderApi.appendInsightsToNote(attachmentId);
      
      if (result.success) {
        // Reload intel to show updated content
        await loadIntel();
      }
    } catch (error: any) {
      console.error('Failed to process attachment:', error);
      alert(error.response?.data?.detail || 'Failed to process document');
    } finally {
      setProcessingAttachmentId(null);
    }
  }, [loadIntel]);

  // Check enrichment status on mount
  useEffect(() => {
    const checkEnrichment = async () => {
      try {
        const status = await apiService.getEnrichmentStatus();
        setEnrichmentEnabled(status.enabled);
      } catch {
        setEnrichmentEnabled(false);
      }
    };
    checkEnrichment();
  }, []);

  // Open enrichment chat for a note
  const handleOpenEnrichChat = useCallback((intel: ParsedIntel) => {
    setEnrichChatOpen(intel);
    setEnrichChatQuery('');
  }, []);

  // Ask a question about the intel entry
  const handleAskEnrichment = useCallback(async () => {
    if (!enrichChatOpen || !enrichChatQuery.trim() || !enrichmentEnabled) return;
    
    const noteId = enrichChatOpen.id;
    const query = enrichChatQuery.trim();
    
    // Add user message to history
    setEnrichChatHistory(prev => {
      const history = prev.get(noteId) || [];
      return new Map(prev).set(noteId, [...history, { type: 'user' as const, content: query }]);
    });
    
    setEnrichChatQuery('');
    setEnrichChatLoading(true);
    
    try {
      // Build context from the note
      const context = [
        `Note Title: ${enrichChatOpen.title}`,
        `Category: ${enrichChatOpen.category}`,
        enrichChatOpen.location ? `Location: ${enrichChatOpen.location.lat}, ${enrichChatOpen.location.lng}` : '',
        enrichChatOpen.location?.name ? `Place: ${enrichChatOpen.location.name}` : '',
        `Summary: ${enrichChatOpen.summary.substring(0, 300)}`,
      ].filter(Boolean).join('\n');
      
      // Search with the user's question + note context
      const searchQuery = `${query} ${context}`;
      const result = await apiService.enrichSearch(searchQuery);
      
      // Add assistant response to history
      setEnrichChatHistory(prev => {
        const history = prev.get(noteId) || [];
        return new Map(prev).set(noteId, [...history, {
          type: 'assistant' as const,
          content: result.summary || 'No results found.',
          sources: result.sources,
        }]);
      });
      
      // Also store in enrichment results for display in the main view
      if (result.success && result.summary) {
        setEnrichmentResults(prev => new Map(prev).set(noteId, result));
      }
      
    } catch (error) {
      console.error('Enrichment query failed:', error);
      setEnrichChatHistory(prev => {
        const history = prev.get(noteId) || [];
        return new Map(prev).set(noteId, [...history, {
          type: 'assistant' as const,
          content: 'Sorry, the search failed. Please try again.',
        }]);
      });
    } finally {
      setEnrichChatLoading(false);
    }
  }, [enrichChatOpen, enrichChatQuery, enrichmentEnabled]);

  // Save enrichment chat to note (append Q&A to note content)
  const handleSaveEnrichmentToNote = useCallback(async () => {
    if (!enrichChatOpen) return;
    
    const noteId = enrichChatOpen.id;
    const chatHistory = enrichChatHistory.get(noteId) || [];
    
    if (chatHistory.length === 0) return;
    
    setEnrichChatLoading(true);
    
    try {
      // Build the enrichment content to append
      const enrichmentContent = chatHistory.map(msg => {
        if (msg.type === 'user') {
          return `\n[RESEARCH Q]: ${msg.content}`;
        } else {
          let response = `\n[RESEARCH A]: ${msg.content}`;
          if (msg.sources && msg.sources.length > 0) {
            response += '\nSources: ' + msg.sources.slice(0, 3).map(s => s.title || s.url).join(', ');
          }
          return response;
        }
      }).join('\n');
      
      // Append to existing note content
      const updatedContent = enrichChatOpen.rawContent + '\n\n---\n[ENRICHMENT DATA - ' + new Date().toLocaleString() + ']' + enrichmentContent;
      
      await pathfinderApi.updateNote(noteId, updatedContent);
      
      // Refresh and close
      await loadIntel();
      setEnrichChatOpen(null);
      // Clear the chat history for this note since it's now saved
      setEnrichChatHistory(prev => {
        const newMap = new Map(prev);
        newMap.delete(noteId);
        return newMap;
      });
      
    } catch (error) {
      console.error('Failed to save enrichment to note:', error);
      alert('Failed to save enrichment to note');
    } finally {
      setEnrichChatLoading(false);
    }
  }, [enrichChatOpen, enrichChatHistory, loadIntel]);

  // Update note title with discovered location name
  const handleUpdateTitle = useCallback(async (noteId: number, enrichment: EnrichmentResponse) => {
    if (!enrichment.discovered_place_name) return;
    
    setUpdatingTitleId(noteId);
    
    try {
      // Build the new title
      const placeName = enrichment.discovered_place_name;
      const city = enrichment.discovered_city;
      const country = enrichment.discovered_country;
      
      // Create a descriptive title
      let newTitle = placeName;
      if (city && city !== placeName) {
        newTitle = `${placeName}, ${city}`;
      }
      
      await pathfinderApi.updateNoteTitle(noteId, newTitle, {
        placeName,
        city: city || undefined,
        country: country || undefined,
      });
      
      // Refresh the intel list to show updated title
      await loadIntel();
      
    } catch (error: any) {
      console.error('Failed to update title:', error);
      alert('Failed to update title: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUpdatingTitleId(null);
    }
  }, [loadIntel]);

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  // Normalize device IDs for comparison (case-insensitive, handle UUID format variations)
  const normalizeDeviceId = useCallback((id: string) => {
    return id.toLowerCase().replace(/-/g, '').trim();
  }, []);

  // Filter intel items
  const filteredIntel = useMemo(() => {
    // Pre-normalize loaded device IDs for faster matching
    const normalizedLoadedIds = loadedDeviceIds.map(id => normalizeDeviceId(id));
    
    return intelItems.filter(item => {
      // Filter by loaded devices on map (if enabled and devices are loaded)
      if (filterByLoadedDevices && loadedDeviceIds.length > 0) {
        const normalizedItemId = normalizeDeviceId(item.deviceId);
        const normalizedItemLabel = normalizeDeviceId(item.deviceLabel);
        
        // Check if any loaded device ID matches against BOTH deviceId and deviceLabel
        // The map uses studentId (which typically matches label), notes store registration_id
        const matchesLoadedDevice = normalizedLoadedIds.some(loadedId => 
          // Match against registration_id (deviceId)
          loadedId === normalizedItemId || 
          loadedId.includes(normalizedItemId) || 
          normalizedItemId.includes(loadedId) ||
          // Match against label (deviceLabel) - this is what map typically shows
          loadedId === normalizedItemLabel ||
          loadedId.includes(normalizedItemLabel) ||
          normalizedItemLabel.includes(loadedId)
        );
        if (!matchesLoadedDevice) {
          return false;
        }
      }
      
      // Filter by map extent (if enabled and bounds are provided)
      if (filterByMapExtent && mapBounds && item.location) {
        const { lat, lng } = item.location;
        const inBounds = 
          lat >= mapBounds.south &&
          lat <= mapBounds.north &&
          lng >= mapBounds.west &&
          lng <= mapBounds.east;
        if (!inBounds) {
          return false;
        }
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          item.title.toLowerCase().includes(query) ||
          item.summary.toLowerCase().includes(query) ||
          item.category.toLowerCase().includes(query) ||
          item.deviceLabel.toLowerCase().includes(query) ||
          (item.createdBy && item.createdBy.toLowerCase().includes(query));
        if (!matchesSearch) return false;
      }

      // Type filter
      if (selectedType !== 'all' && item.type !== selectedType) {
        return false;
      }

      // Category filter
      if (selectedCategory && item.category !== selectedCategory) {
        return false;
      }

      // Device filter (manual selection)
      if (selectedDevice && item.deviceId !== selectedDevice) {
        return false;
      }

      return true;
    });
  }, [intelItems, searchQuery, selectedType, selectedCategory, selectedDevice, filterByLoadedDevices, loadedDeviceIds, filterByMapExtent, mapBounds, normalizeDeviceId]);

  // Get unique categories and devices for filters
  const { categories, devices } = useMemo(() => {
    const cats = new Set<string>();
    const devs = new Map<string, string>();
    
    intelItems.forEach(item => {
      cats.add(item.category);
      devs.set(item.deviceId, item.deviceLabel);
    });

    return {
      categories: Array.from(cats).sort(),
      devices: Array.from(devs.entries()).map(([id, label]) => ({ id, label })),
    };
  }, [intelItems]);

  // Statistics
  const stats = useMemo(() => {
    const categoryCount: Record<string, number> = {};
    const deviceCount: Record<string, number> = {};
    const sentimentCount: Record<string, number> = {};
    let overwatchCount = 0;
    let polCount = 0;
    let userNoteCount = 0;
    
    filteredIntel.forEach(item => {
      categoryCount[item.category] = (categoryCount[item.category] || 0) + 1;
      deviceCount[item.deviceId] = (deviceCount[item.deviceId] || 0) + 1;
      if (item.sentiment) {
        sentimentCount[item.sentiment] = (sentimentCount[item.sentiment] || 0) + 1;
      }
      if (item.type === 'pol') {
        polCount++;
      } else if (item.type === 'overwatch') {
        overwatchCount++;
      } else {
        userNoteCount++;
      }
    });

    // Find most common category
    const topCategory = Object.entries(categoryCount)
      .sort(([,a], [,b]) => b - a)[0];

    return {
      total: filteredIntel.length,
      categoryCount,
      deviceCount,
      sentimentCount,
      topCategory: topCategory ? topCategory[0] : null,
      devicesWithIntel: Object.keys(deviceCount).length,
      overwatchCount,
      polCount,
      userNoteCount,
    };
  }, [filteredIntel]);

  // Delete intel item
  const handleDelete = async (item: ParsedIntel) => {
    if (!window.confirm(`Delete this intel item?\n\n"${item.title}"`)) {
      return;
    }

    try {
      await pathfinderApi.deleteNote(item.id);
      setIntelItems(prev => prev.filter(i => i.id !== item.id));
    } catch (error) {
      console.error('Failed to delete intel:', error);
      alert('Failed to delete intel item');
    }
  };

  // Open edit dialog
  const handleEditOpen = (item: ParsedIntel) => {
    setEditingNote(item);
    setEditedContent(item.rawContent);
  };

  // Save edited note
  const handleSaveEdit = async () => {
    if (!editingNote) return;
    
    setSavingEdit(true);
    try {
      await pathfinderApi.updateNote(editingNote.id, editedContent);
      // Refresh the intel list to show updated content
      await loadIntel();
      setEditingNote(null);
      setEditedContent('');
    } catch (error) {
      console.error('Failed to update note:', error);
      alert('Failed to update note');
    } finally {
      setSavingEdit(false);
    }
  };

  // Delete all intel items (filtered or all)
  const handleDeleteAll = async () => {
    setDeletingAll(true);
    try {
      // Delete all filtered items
      const deletePromises = filteredIntel.map(item => 
        pathfinderApi.deleteNote(item.id).catch(err => {
          console.error(`Failed to delete note ${item.id}:`, err);
          return null;
        })
      );
      
      await Promise.all(deletePromises);
      
      // Refresh the list
      await loadIntel();
      setDeleteAllConfirmOpen(false);
    } catch (error) {
      console.error('Failed to delete all:', error);
      alert('Failed to delete some items');
    } finally {
      setDeletingAll(false);
    }
  };

  // Create a new note (optionally with file attachment)
  const handleCreateNote = async () => {
    if (!newNoteDeviceId) return;
    // Allow either content or file (or both)
    if (!newNoteContent.trim() && !newNoteFile) return;
    
    setSavingNewNote(true);
    try {
      // Build note content
      let noteContent = newNoteContent.trim();
      
      // If only a file is provided, create a simple note
      if (!noteContent && newNoteFile) {
        noteContent = `[DOCUMENT UPLOAD]\n\nFile: ${newNoteFile.name}\nUploaded: ${new Date().toLocaleString()}`;
      }
      
      // Create the note first
      const newNote = await pathfinderApi.createNote(noteContent, newNoteDeviceId as number);
      
      // If a file was selected, upload it as an attachment
      if (newNoteFile && newNote.id) {
        try {
          await pathfinderApi.uploadNoteAttachment(newNote.id, newNoteFile);
        } catch (uploadError) {
          console.error('Failed to upload attachment:', uploadError);
          // Note was created, but attachment failed - don't fail completely
          alert('Note created but file upload failed. You can try attaching the file again.');
        }
      }
      
      await loadIntel();
      setAddNoteOpen(false);
      setNewNoteContent('');
      setNewNoteDeviceId('');
      setNewNoteFile(null);
    } catch (error) {
      console.error('Failed to create note:', error);
      alert('Failed to create note');
    } finally {
      setSavingNewNote(false);
    }
  };

  // Get unique devices from intel items for the dropdown
  const availableDevices = useMemo(() => {
    const deviceMap = new Map<number, { id: number; label: string }>();
    intelItems.forEach(item => {
      if (!deviceMap.has(item.watchlistId)) {
        deviceMap.set(item.watchlistId, {
          id: item.watchlistId,
          label: item.deviceLabel || item.deviceId,
        });
      }
    });
    return Array.from(deviceMap.values());
  }, [intelItems]);

  // Toggle item expansion
  const toggleExpanded = (id: number) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        // Load attachments when expanding
        loadAttachments(id);
      }
      return next;
    });
  };

  // Export functions
  const handleExport = (format: 'json' | 'csv' | 'markdown' | 'pdf') => {
    setExportAnchor(null);
    
    const exportData = filteredIntel.map(item => ({
      title: item.title,
      type: item.type,
      date: item.date,
      category: item.category,
      sentiment: item.sentiment || '',
      location: item.location ? `${item.location.lat}, ${item.location.lng}` : '',
      locationName: item.location?.name || '',
      summary: item.summary,
      sourceLink: item.sourceLink || '',
      deviceId: item.deviceId,
      deviceLabel: item.deviceLabel,
      createdBy: item.createdBy || '',
      addedAt: item.addedAt,
    }));

    // PDF Export - opens print dialog
    if (format === 'pdf') {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Please allow popups to export PDF');
        return;
      }
      
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Intelligence Log - ${new Date().toLocaleDateString()}</title>
          <style>
            @media print {
              @page { margin: 0.5in; size: letter; }
            }
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { 
              font-family: 'Segoe UI', Arial, sans-serif; 
              font-size: 10pt;
              line-height: 1.4;
              color: #1e293b;
              padding: 20px;
            }
            .header { 
              border-bottom: 3px solid #3b82f6;
              padding-bottom: 12px;
              margin-bottom: 20px;
            }
            .header h1 { 
              font-size: 18pt; 
              font-weight: 700;
              color: #0f172a;
              letter-spacing: 0.5px;
            }
            .header .meta {
              font-size: 9pt;
              color: #64748b;
              margin-top: 4px;
            }
            .summary-bar {
              display: flex;
              gap: 24px;
              background: #f1f5f9;
              padding: 10px 16px;
              border-radius: 4px;
              margin-bottom: 20px;
            }
            .summary-item { text-align: center; }
            .summary-item .value { font-size: 16pt; font-weight: 700; color: #3b82f6; }
            .summary-item .label { font-size: 8pt; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
            .entry { 
              border: 1px solid #e2e8f0;
              border-left: 4px solid #3b82f6;
              margin-bottom: 12px;
              padding: 12px;
              page-break-inside: avoid;
            }
            .entry.osint { border-left-color: #f59e0b; }
            .entry.humint { border-left-color: #3b82f6; }
            .entry-header { 
              display: flex; 
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 8px;
            }
            .entry-title { 
              font-size: 11pt; 
              font-weight: 600;
              color: #0f172a;
              flex: 1;
            }
            .entry-meta {
              display: flex;
              gap: 12px;
              flex-wrap: wrap;
              font-size: 8pt;
              color: #64748b;
              margin-bottom: 8px;
            }
            .entry-meta span { background: #f1f5f9; padding: 2px 6px; border-radius: 2px; }
            .entry-meta .type-osint { background: #fef3c7; color: #b45309; }
            .entry-meta .type-pol { background: #d1fae5; color: #047857; }
            .entry-meta .type-humint { background: #dbeafe; color: #1d4ed8; }
            .entry-meta .category { background: #f1f5f9; color: #475569; font-weight: 600; }
            .entry-summary { 
              font-size: 9pt;
              color: #334155;
              line-height: 1.5;
              white-space: pre-wrap;
            }
            .entry-footer {
              margin-top: 8px;
              font-size: 8pt;
              color: #94a3b8;
              border-top: 1px solid #e2e8f0;
              padding-top: 6px;
            }
            .footer {
              margin-top: 24px;
              padding-top: 12px;
              border-top: 1px solid #e2e8f0;
              font-size: 8pt;
              color: #94a3b8;
              text-align: center;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>INTELLIGENCE LOG</h1>
            <div class="meta">Generated: ${new Date().toLocaleString()} | Entries: ${exportData.length}</div>
          </div>
          
          <div class="summary-bar">
            <div class="summary-item">
              <div class="value">${exportData.length}</div>
              <div class="label">Total Entries</div>
            </div>
            <div class="summary-item">
              <div class="value">${exportData.filter(e => e.type === 'pol').length}</div>
              <div class="label">POL</div>
            </div>
            <div class="summary-item">
              <div class="value">${exportData.filter(e => e.type === 'overwatch').length}</div>
              <div class="label">OSINT</div>
            </div>
            <div class="summary-item">
              <div class="value">${exportData.filter(e => e.type === 'user').length}</div>
              <div class="label">HUMINT</div>
            </div>
            <div class="summary-item">
              <div class="value">${new Set(exportData.map(e => e.deviceId)).size}</div>
              <div class="label">Targets</div>
            </div>
          </div>
          
          ${exportData.map((item, idx) => `
            <div class="entry ${item.type === 'pol' ? 'pol' : (item.type === 'overwatch' ? 'osint' : 'humint')}">
              <div class="entry-header">
                <div class="entry-title">${idx + 1}. ${item.title.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>
              </div>
              <div class="entry-meta">
                <span class="${item.type === 'pol' ? 'type-pol' : (item.type === 'overwatch' ? 'type-osint' : 'type-humint')}">${item.type === 'pol' ? 'POL' : (item.type === 'overwatch' ? 'OSINT' : 'HUMINT')}</span>
                <span class="category">${item.category}</span>
                <span>📅 ${item.date}</span>
                ${item.sentiment ? `<span>📊 ${item.sentiment}</span>` : ''}
              </div>
              <div class="entry-summary">${item.summary.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>
              <div class="entry-footer">
                <strong>Target:</strong> ${item.deviceId} 
                ${item.createdBy ? `| <strong>Analyst:</strong> ${item.createdBy}` : ''} 
                | <strong>Added:</strong> ${new Date(item.addedAt).toLocaleString()}
                ${item.location ? `| <strong>Location:</strong> ${item.location}` : ''}
              </div>
            </div>
          `).join('')}
          
          <div class="footer">
            Intelligence Log Export - OSINT Explorer - CONFIDENTIAL
          </div>
        </body>
        </html>
      `;
      
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
      return;
    }

    let content: string;
    let filename: string;
    let mimeType: string;

    if (format === 'json') {
      content = JSON.stringify(exportData, null, 2);
      filename = `intel-packet-${new Date().toISOString().split('T')[0]}.json`;
      mimeType = 'application/json';
    } else if (format === 'csv') {
      const headers = ['Title', 'Type', 'Date', 'Category', 'Sentiment', 'Location', 'Location Name', 'Summary', 'Source Link', 'Device ID', 'Device Label', 'Created By', 'Added At'];
      const rows = exportData.map(item => [
        `"${item.title.replace(/"/g, '""')}"`,
        `"${item.type}"`,
        `"${item.date}"`,
        `"${item.category}"`,
        `"${item.sentiment}"`,
        `"${item.location}"`,
        `"${item.locationName}"`,
        `"${item.summary.replace(/"/g, '""').replace(/\n/g, ' ')}"`,
        `"${item.sourceLink}"`,
        `"${item.deviceId}"`,
        `"${item.deviceLabel}"`,
        `"${item.createdBy}"`,
        `"${item.addedAt}"`,
      ]);
      content = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
      filename = `intel-packet-${new Date().toISOString().split('T')[0]}.csv`;
      mimeType = 'text/csv';
    } else {
      // Markdown
      content = `# Intel Packet Export\n\nGenerated: ${new Date().toLocaleString()}\n\nTotal Items: ${filteredIntel.length}\n\n---\n\n`;
      exportData.forEach((item, idx) => {
        content += `## ${idx + 1}. ${item.title}\n\n`;
        content += `- **Type:** ${item.type === 'pol' ? 'POL' : (item.type === 'overwatch' ? 'OSINT' : 'HUMINT')}\n`;
        content += `- **Date:** ${item.date}\n`;
        content += `- **Category:** ${item.category}\n`;
        if (item.sentiment) content += `- **Sentiment:** ${item.sentiment}\n`;
        if (item.location) content += `- **Location:** ${item.location}${item.locationName ? ` (${item.locationName})` : ''}\n`;
        content += `- **Target ID:** ${item.deviceId}\n`;
        content += `- **Target Label:** ${item.deviceLabel}\n`;
        if (item.createdBy) content += `- **Analyst:** ${item.createdBy}\n`;
        content += `\n### Summary\n\n${item.summary}\n\n`;
        if (item.sourceLink) content += `[View Original Source](${item.sourceLink})\n\n`;
        content += '---\n\n';
      });
      filename = `intel-packet-${new Date().toISOString().split('T')[0]}.md`;
      mimeType = 'text/markdown';
    }

    // Download file
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Get sentiment color
  const getSentimentColor = (sentiment?: string) => {
    if (!sentiment) return colors.textSecondary;
    const s = sentiment.toLowerCase();
    if (s.includes('positive')) return colors.positive;
    if (s.includes('negative')) return colors.negative;
    return colors.neutral;
  };

  // Get category color - intelligence-style
  const getCategoryColor = (category: string) => {
    const categoryColors: Record<string, string> = {
      'Crime': '#ef4444',
      'Drugs': '#a855f7',
      'Military': '#3b82f6',
      'Security': '#14b8a6',
      'Terrorism': '#dc2626',
      'Border': '#f97316',
      'Violence': '#dc2626',
      'Politics': '#8b5cf6',
      'User Note': '#3b82f6',
      'Unknown': '#6b7280',
    };
    return categoryColors[category] || '#6b7280';
  };

  // Format timestamp for log entries
  const formatLogTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).toUpperCase();
  };

  // Generate log entry ID
  const getLogEntryId = (item: ParsedIntel, index: number) => {
    const prefix = item.type === 'overwatch' ? 'OW' : 'UN';
    return `${prefix}-${String(filteredIntel.length - index).padStart(4, '0')}`;
  };

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden',
      bgcolor: colors.bg,
      fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", monospace',
    }}>
      {/* Header - Classification Banner */}
      <Box sx={{ 
        px: 2, 
        py: 1.5,
        background: isDark 
          ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        borderBottom: `2px solid ${colors.primary}`,
      }}>
        {/* Top bar with title and actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SecurityIcon sx={{ color: colors.primary, fontSize: 22 }} />
            <Typography sx={{ 
              color: colors.text, 
              fontWeight: 700,
              fontSize: '0.95rem',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}>
              Intelligence Log
            </Typography>
            <Box sx={{
              bgcolor: colors.primary,
              color: '#fff',
              px: 1,
              py: 0.25,
              borderRadius: '4px',
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: '0.05em',
            }}>
              {filteredIntel.length} ENTRIES
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Refresh Log">
              <IconButton 
                size="small" 
                onClick={loadIntel} 
                disabled={loading}
                sx={{ 
                  color: colors.textSecondary,
                  '&:hover': { color: colors.primary, bgcolor: alpha(colors.primary, 0.1) },
                }}
              >
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export">
              <IconButton 
                size="small" 
                onClick={(e) => setExportAnchor(e.currentTarget)}
                disabled={filteredIntel.length === 0}
                sx={{ 
                  color: colors.textSecondary,
                  '&:hover': { color: colors.primary, bgcolor: alpha(colors.primary, 0.1) },
                }}
              >
                <ExportIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={exportAnchor}
              open={Boolean(exportAnchor)}
              onClose={() => setExportAnchor(null)}
              PaperProps={{
                sx: {
                  bgcolor: colors.surface,
                  border: `1px solid ${colors.border}`,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                }
              }}
            >
              <MenuItem onClick={() => handleExport('json')}>
                <ListItemIcon><ArticleIcon fontSize="small" sx={{ color: colors.textSecondary }} /></ListItemIcon>
                <ListItemText sx={{ '& .MuiTypography-root': { fontSize: '0.85rem' } }}>Export as JSON</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleExport('csv')}>
                <ListItemIcon><ArticleIcon fontSize="small" sx={{ color: colors.textSecondary }} /></ListItemIcon>
                <ListItemText sx={{ '& .MuiTypography-root': { fontSize: '0.85rem' } }}>Export as CSV</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleExport('markdown')}>
                <ListItemIcon><ArticleIcon fontSize="small" sx={{ color: colors.textSecondary }} /></ListItemIcon>
                <ListItemText sx={{ '& .MuiTypography-root': { fontSize: '0.85rem' } }}>Export as Markdown</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleExport('pdf')}>
                <ListItemIcon><ArticleIcon fontSize="small" sx={{ color: colors.textSecondary }} /></ListItemIcon>
                <ListItemText sx={{ '& .MuiTypography-root': { fontSize: '0.85rem' } }}>Export as PDF</ListItemText>
              </MenuItem>
            </Menu>
            <Tooltip title="Add new note">
              <IconButton 
                size="small" 
                onClick={() => setAddNoteOpen(true)}
                sx={{ 
                  color: colors.textSecondary,
                  '&:hover': { color: colors.success, bgcolor: alpha(colors.success, 0.1) },
                }}
              >
                <AddNoteIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete all filtered items">
              <IconButton 
                size="small" 
                onClick={() => setDeleteAllConfirmOpen(true)}
                disabled={filteredIntel.length === 0}
                sx={{ 
                  color: colors.textSecondary,
                  '&:hover': { color: colors.negative, bgcolor: alpha(colors.negative, 0.1) },
                }}
              >
                <DeleteAllIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Search */}
        <TextField
          fullWidth
          size="small"
          placeholder="SEARCH INTELLIGENCE RECORDS..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colors.textMuted, fontSize: 18 }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <ClearIcon sx={{ fontSize: 14, color: colors.textMuted }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ 
            mb: 1.5,
            '& .MuiOutlinedInput-root': {
              bgcolor: colors.surface,
              borderRadius: '6px',
              fontSize: '0.8rem',
              letterSpacing: '0.03em',
              '& fieldset': { borderColor: colors.border },
              '&:hover fieldset': { borderColor: colors.borderAccent },
              '&.Mui-focused fieldset': { borderColor: colors.primary },
            },
            '& input::placeholder': {
              color: colors.textMuted,
              opacity: 1,
              fontSize: '0.75rem',
              letterSpacing: '0.05em',
            }
          }}
        />

        {/* Compact Filter Row - All dropdowns in one line */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Type filter dropdown */}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value as 'all' | 'pol' | 'overwatch' | 'user')}
              sx={{
                height: 26,
                fontSize: '0.65rem',
                fontWeight: 600,
                letterSpacing: '0.05em',
                bgcolor: colors.surface,
                color: selectedType === 'all' ? colors.primary : selectedType === 'pol' ? colors.pol : selectedType === 'overwatch' ? colors.overwatch : colors.userNote,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: selectedType !== 'all' ? (selectedType === 'pol' ? colors.pol : selectedType === 'overwatch' ? colors.overwatch : colors.userNote) : colors.border,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                '& .MuiSelect-select': { py: 0.25, px: 1 },
                '& .MuiSelect-icon': { color: colors.textMuted, fontSize: 16, right: 4 },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: colors.surface,
                    border: `1px solid ${colors.border}`,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                    '& .MuiMenuItem-root': { fontSize: '0.65rem', py: 0.5 },
                  },
                },
              }}
            >
              <MenuItem value="all">ALL ({stats.total})</MenuItem>
              <MenuItem value="pol" sx={{ color: colors.pol }}>POL ({stats.polCount})</MenuItem>
              <MenuItem value="overwatch" sx={{ color: colors.overwatch }}>OSINT ({stats.overwatchCount})</MenuItem>
              <MenuItem value="user" sx={{ color: colors.userNote }}>HUMINT ({stats.userNoteCount})</MenuItem>
            </Select>
          </FormControl>

          {/* Category filter dropdown */}
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value as string)}
              displayEmpty
              sx={{
                height: 26,
                fontSize: '0.65rem',
                fontWeight: 600,
                letterSpacing: '0.05em',
                bgcolor: colors.surface,
                color: selectedCategory ? getCategoryColor(selectedCategory) : colors.textMuted,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: selectedCategory ? getCategoryColor(selectedCategory) : colors.border,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                '& .MuiSelect-select': { py: 0.25, px: 1 },
                '& .MuiSelect-icon': { color: colors.textMuted, fontSize: 16, right: 4 },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: colors.surface,
                    border: `1px solid ${colors.border}`,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                    '& .MuiMenuItem-root': { fontSize: '0.65rem', py: 0.5 },
                  },
                },
              }}
            >
              <MenuItem value="">CATEGORY</MenuItem>
              {categories.map(cat => (
                <MenuItem key={cat} value={cat} sx={{ color: getCategoryColor(cat) }}>
                  {cat.toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Target/Device filter dropdown */}
          <FormControl size="small" sx={{ minWidth: 120, maxWidth: 200 }}>
            <Select
              value={selectedDevice}
              onChange={(e) => setSelectedDevice(e.target.value as string)}
              displayEmpty
              sx={{
                height: 26,
                fontSize: '0.65rem',
                fontWeight: 600,
                letterSpacing: '0.05em',
                bgcolor: colors.surface,
                color: selectedDevice ? colors.primary : colors.textMuted,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: selectedDevice ? colors.primary : colors.border,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                '& .MuiSelect-select': { py: 0.25, px: 1 },
                '& .MuiSelect-icon': { color: colors.textMuted, fontSize: 16, right: 4 },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: colors.surface,
                    border: `1px solid ${colors.border}`,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                    maxHeight: 300,
                    '& .MuiMenuItem-root': { 
                      fontSize: '0.6rem', 
                      py: 0.5,
                      fontFamily: '"JetBrains Mono", monospace',
                    },
                  },
                },
              }}
            >
              <MenuItem value="">TARGET ({devices.length})</MenuItem>
              {devices.map(dev => (
                <MenuItem key={dev.id} value={dev.id}>
                  {dev.id.length > 20 ? dev.id.substring(0, 8) + '...' + dev.id.slice(-8) : dev.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Map Data toggle */}
          <Tooltip title={loadedDeviceIds.length > 0 
            ? `Show only ${loadedDeviceIds.length} device(s) on map` 
            : 'Load data on map to filter'
          }>
            <Box 
              onClick={() => loadedDeviceIds.length > 0 && setFilterByLoadedDevices(!filterByLoadedDevices)}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                px: 1,
                py: 0.25,
                height: 26,
                borderRadius: '4px',
                border: `1px solid ${filterByLoadedDevices && loadedDeviceIds.length > 0 ? colors.primary : colors.border}`,
                bgcolor: filterByLoadedDevices && loadedDeviceIds.length > 0 ? alpha(colors.primary, 0.15) : colors.surface,
                cursor: loadedDeviceIds.length > 0 ? 'pointer' : 'default',
                opacity: loadedDeviceIds.length > 0 ? 1 : 0.5,
                transition: 'all 0.15s',
                '&:hover': loadedDeviceIds.length > 0 ? {
                  borderColor: colors.primary,
                  bgcolor: alpha(colors.primary, 0.1),
                } : {},
              }}
            >
              <Box sx={{
                width: 8,
                height: 8,
                borderRadius: '2px',
                bgcolor: filterByLoadedDevices && loadedDeviceIds.length > 0 ? colors.primary : colors.textMuted,
                transition: 'all 0.15s',
              }} />
              <Typography sx={{ 
                fontSize: '0.6rem', 
                fontWeight: 600, 
                letterSpacing: '0.05em',
                color: filterByLoadedDevices && loadedDeviceIds.length > 0 ? colors.primary : colors.textMuted,
                whiteSpace: 'nowrap',
              }}>
                MAP {loadedDeviceIds.length > 0 ? `(${loadedDeviceIds.length})` : ''}
              </Typography>
            </Box>
          </Tooltip>

          {/* Filter by map extent toggle */}
          <Tooltip title={
            mapBounds 
              ? filterByMapExtent 
                ? 'Click to show all locations' 
                : 'Click to show only locations in map view'
              : 'Pan/zoom map to enable extent filter'
          }>
            <Box 
              onClick={() => mapBounds && setFilterByMapExtent(!filterByMapExtent)}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                px: 1,
                py: 0.25,
                height: 26,
                borderRadius: '4px',
                border: `1px solid ${filterByMapExtent && mapBounds ? colors.accent : colors.border}`,
                bgcolor: filterByMapExtent && mapBounds ? alpha(colors.accent, 0.15) : colors.surface,
                cursor: mapBounds ? 'pointer' : 'default',
                opacity: mapBounds ? 1 : 0.5,
                transition: 'all 0.15s',
                '&:hover': mapBounds ? {
                  borderColor: colors.accent,
                  bgcolor: alpha(colors.accent, 0.1),
                } : {},
              }}
            >
              <ZoomToIcon sx={{ 
                fontSize: 12,
                color: filterByMapExtent && mapBounds ? colors.accent : colors.textMuted,
              }} />
              <Typography sx={{ 
                fontSize: '0.6rem', 
                fontWeight: 600, 
                letterSpacing: '0.05em',
                color: filterByMapExtent && mapBounds ? colors.accent : colors.textMuted,
                whiteSpace: 'nowrap',
              }}>
                EXTENT
              </Typography>
            </Box>
          </Tooltip>

          {/* Clear all filters button */}
          {(selectedType !== 'all' || selectedCategory || selectedDevice) && (
            <Tooltip title="Clear all filters">
              <IconButton 
                size="small" 
                onClick={() => {
                  setSelectedType('all');
                  setSelectedCategory('');
                  setSelectedDevice('');
                }}
                sx={{ 
                  p: 0.25,
                  color: colors.textMuted,
                  '&:hover': { color: colors.negative },
                }}
              >
                <ClearIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Summary Stats Panel */}
      <Collapse in={showStats && !loading && intelItems.length > 0}>
        <Box sx={{ 
          p: 1.5, 
          borderBottom: `1px solid ${colors.border}`,
          bgcolor: alpha(colors.primary, 0.03),
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ 
                  color: colors.primary, 
                  fontWeight: 800,
                  fontSize: '1.5rem',
                  fontFamily: '"JetBrains Mono", monospace',
                  lineHeight: 1,
                }}>
                  {stats.total}
                </Typography>
                <Typography sx={{ 
                  color: colors.textMuted, 
                  fontSize: '0.6rem',
                  letterSpacing: '0.1em',
                  fontWeight: 600,
                  mt: 0.25,
                }}>
                  TOTAL
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ 
                  color: colors.userNote, 
                  fontWeight: 800,
                  fontSize: '1.5rem',
                  fontFamily: '"JetBrains Mono", monospace',
                  lineHeight: 1,
                }}>
                  {stats.devicesWithIntel}
                </Typography>
                <Typography sx={{ 
                  color: colors.textMuted, 
                  fontSize: '0.6rem',
                  letterSpacing: '0.1em',
                  fontWeight: 600,
                  mt: 0.25,
                }}>
                  TARGETS
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ 
                  color: colors.overwatch, 
                  fontWeight: 800,
                  fontSize: '1.5rem',
                  fontFamily: '"JetBrains Mono", monospace',
                  lineHeight: 1,
                }}>
                  {categories.length}
                </Typography>
                <Typography sx={{ 
                  color: colors.textMuted, 
                  fontSize: '0.6rem',
                  letterSpacing: '0.1em',
                  fontWeight: 600,
                  mt: 0.25,
                }}>
                  CATEGORIES
                </Typography>
              </Box>
              {stats.topCategory && (
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{
                    px: 1,
                    py: 0.25,
                    bgcolor: alpha(getCategoryColor(stats.topCategory), 0.15),
                    color: getCategoryColor(stats.topCategory),
                    borderRadius: '4px',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                  }}>
                    {stats.topCategory.toUpperCase()}
                  </Box>
                  <Typography sx={{ 
                    color: colors.textMuted, 
                    fontSize: '0.6rem',
                    letterSpacing: '0.1em',
                    fontWeight: 600,
                    mt: 0.5,
                  }}>
                    PRIMARY
                  </Typography>
                </Box>
              )}
            </Box>
            <IconButton 
              size="small" 
              onClick={() => setShowStats(false)}
              sx={{ color: colors.textMuted }}
            >
              <CollapseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      </Collapse>

      {/* Show stats toggle */}
      {!showStats && intelItems.length > 0 && (
        <Box sx={{ 
          px: 2, 
          py: 0.5, 
          borderBottom: `1px solid ${colors.border}`,
          bgcolor: colors.surface,
        }}>
          <Button
            size="small"
            onClick={() => setShowStats(true)}
            sx={{ 
              textTransform: 'uppercase', 
              color: colors.textMuted,
              fontSize: '0.65rem',
              fontWeight: 600,
              letterSpacing: '0.1em',
            }}
          >
            ▼ Show Summary
          </Button>
        </Box>
      )}

      {/* Main Content - Log Entries */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
            <CircularProgress size={28} sx={{ color: colors.primary }} />
            <Typography sx={{ 
              color: colors.textMuted, 
              mt: 2,
              fontSize: '0.75rem',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}>
              Loading Intelligence Records...
            </Typography>
          </Box>
        ) : intelItems.length === 0 ? (
          <Box sx={{ 
            textAlign: 'center', 
            py: 8,
            px: 3,
          }}>
            <SecurityIcon sx={{ fontSize: 56, color: colors.border, mb: 2 }} />
            <Typography sx={{ 
              color: colors.text, 
              fontWeight: 700,
              fontSize: '1rem',
              letterSpacing: '0.05em',
              mb: 1,
            }}>
              NO INTELLIGENCE COLLECTED
            </Typography>
            <Typography sx={{ 
              color: colors.textMuted, 
              fontSize: '0.8rem',
              maxWidth: 320, 
              mx: 'auto',
              lineHeight: 1.6,
            }}>
              Save articles from the Overwatch tab to build your intelligence log. 
              Click "Save to Intel Packet" on any article to begin.
            </Typography>
          </Box>
        ) : filteredIntel.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <SearchIcon sx={{ fontSize: 44, color: colors.border, mb: 1 }} />
            <Typography sx={{ 
              color: colors.textMuted,
              fontSize: '0.8rem',
              letterSpacing: '0.03em',
            }}>
              No records match current filters
            </Typography>
            <Button 
              size="small" 
              onClick={() => { setSearchQuery(''); setSelectedCategory(''); setSelectedDevice(''); setSelectedType('all'); }}
              sx={{ 
                mt: 1.5,
                textTransform: 'uppercase',
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: '0.1em',
              }}
            >
              Clear Filters
            </Button>
          </Box>
        ) : (
          <Box sx={{ py: 0.5 }}>
            {/* Log entries */}
            {filteredIntel.map((item, index) => (
              <Box
                key={item.id}
                sx={{
                  borderBottom: `1px solid ${colors.border}`,
                  transition: 'background 0.15s',
                  '&:hover': {
                    bgcolor: colors.surfaceHover,
                  },
                }}
              >
                {/* Entry header row */}
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    px: 2,
                    py: 1,
                    gap: 1.5,
                    cursor: 'pointer',
                  }}
                  onClick={() => toggleExpanded(item.id)}
                >
                  {/* Log entry indicator */}
                  <Box sx={{
                    width: 8,
                    height: 8,
                    borderRadius: item.type === 'overwatch' ? '2px' : '50%',
                    bgcolor: item.type === 'overwatch' ? colors.overwatch : colors.userNote,
                    flexShrink: 0,
                  }} />

                  {/* Entry ID */}
                  <Typography sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: colors.textMuted,
                    minWidth: 70,
                    flexShrink: 0,
                  }}>
                    {getLogEntryId(item, index)}
                  </Typography>

                  {/* Timestamp */}
                  <Typography sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.65rem',
                    color: colors.textMuted,
                    minWidth: 140,
                    flexShrink: 0,
                  }}>
                    {formatLogTime(item.addedAt)}
                  </Typography>

                  {/* Category badge */}
                  <Box sx={{
                    px: 1,
                    py: 0.25,
                    bgcolor: alpha(getCategoryColor(item.category), 0.15),
                    color: getCategoryColor(item.category),
                    borderRadius: '3px',
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    flexShrink: 0,
                  }}>
                    {item.category.toUpperCase()}
                  </Box>

                  {/* Sentiment indicator */}
                  {item.sentiment && (
                    <Box sx={{
                      px: 0.75,
                      py: 0.25,
                      bgcolor: alpha(getSentimentColor(item.sentiment), 0.15),
                      color: getSentimentColor(item.sentiment),
                      borderRadius: '3px',
                      fontSize: '0.55rem',
                      fontWeight: 600,
                      letterSpacing: '0.03em',
                      flexShrink: 0,
                    }}>
                      {item.sentiment.toUpperCase()}
                    </Box>
                  )}

                  {/* Title */}
                  <Typography sx={{
                    flex: 1,
                    color: colors.text,
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {item.title}
                  </Typography>

                  {/* Actions */}
                  <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                    <IconButton 
                      size="small" 
                      onClick={(e) => { e.stopPropagation(); toggleExpanded(item.id); }}
                      sx={{ 
                        p: 0.5,
                        color: colors.textMuted,
                        '&:hover': { color: colors.primary },
                      }}
                    >
                      {expandedItems.has(item.id) ? (
                        <CollapseIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <ExpandIcon sx={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                    <Tooltip title="Edit note">
                      <IconButton 
                        size="small" 
                        onClick={(e) => { e.stopPropagation(); handleEditOpen(item); }}
                        sx={{ 
                          p: 0.5,
                          color: colors.textMuted,
                          '&:hover': { color: colors.primary },
                        }}
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete note">
                      <IconButton 
                        size="small" 
                        onClick={(e) => { e.stopPropagation(); handleDelete(item); }}
                        sx={{ 
                          p: 0.5,
                          color: colors.textMuted,
                          '&:hover': { color: colors.negative },
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Expanded content */}
                <Collapse in={expandedItems.has(item.id)}>
                  <Box sx={{ 
                    px: 2, 
                    pb: 2,
                    pl: 11.5, // Align with title
                    borderLeft: `3px solid ${item.type === 'overwatch' ? colors.overwatch : colors.userNote}`,
                    ml: 2,
                  }}>
                    {/* Meta info row */}
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 2, 
                      mb: 1.5, 
                      flexWrap: 'wrap',
                      alignItems: 'center',
                    }}>
                      {/* Target ID */}
                      <Tooltip title={`Full ID: ${item.deviceId}`} placement="top">
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.5,
                          cursor: 'help',
                        }}>
                          <TargetIcon sx={{ fontSize: 12, color: colors.textMuted }} />
                          <Typography sx={{
                            fontFamily: '"JetBrains Mono", monospace',
                            fontSize: '0.65rem',
                            color: colors.textMuted,
                          }}>
                            {item.deviceId}
                          </Typography>
                        </Box>
                      </Tooltip>

                      {/* Source date */}
                      {item.date && (
                        <Typography sx={{
                          fontSize: '0.7rem',
                          color: colors.textMuted,
                        }}>
                          Source Date: <span style={{ color: colors.text }}>{item.date}</span>
                        </Typography>
                      )}

                      {/* Creator */}
                      {item.createdBy && (
                        <Typography sx={{
                          fontSize: '0.7rem',
                          color: colors.textMuted,
                        }}>
                          Analyst: <span style={{ color: colors.text }}>{item.createdBy}</span>
                        </Typography>
                      )}

                      {/* Type badge */}
                      <Box sx={{
                        px: 0.75,
                        py: 0.25,
                        border: `1px solid ${item.type === 'overwatch' ? colors.overwatch : colors.userNote}`,
                        borderRadius: '3px',
                        fontSize: '0.55rem',
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        color: item.type === 'pol' ? colors.pol : (item.type === 'overwatch' ? colors.overwatch : colors.userNote),
                      }}>
                        {item.type === 'pol' ? 'POL' : (item.type === 'overwatch' ? 'OSINT' : 'HUMINT')}
                      </Box>
                    </Box>

                    {/* Summary content */}
                    <Box sx={{
                      bgcolor: alpha(colors.primary, 0.03),
                      border: `1px solid ${colors.border}`,
                      borderRadius: '4px',
                      p: 1.5,
                      mb: 1.5,
                    }}>
                      <Typography sx={{
                        fontSize: '0.8rem',
                        color: colors.text,
                        lineHeight: 1.7,
                        whiteSpace: 'pre-wrap',
                      }}>
                        {item.summary}
                      </Typography>
                    </Box>

                    {/* Location and source */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                      {item.location && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationIcon sx={{ fontSize: 14, color: colors.textMuted }} />
                          <Typography 
                            component="span"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onZoomToLocation && item.location) {
                                onZoomToLocation(item.location.lat, item.location.lng, 16);
                              }
                            }}
                            sx={{
                              fontFamily: '"JetBrains Mono", monospace',
                              fontSize: '0.7rem',
                              color: onZoomToLocation ? colors.primary : colors.textSecondary,
                              cursor: onZoomToLocation ? 'pointer' : 'default',
                              '&:hover': onZoomToLocation ? {
                                textDecoration: 'underline',
                                color: colors.accent,
                              } : {},
                            }}
                          >
                            {item.location.lat.toFixed(4)}, {item.location.lng.toFixed(4)}
                            {item.location.name && (
                              <span style={{ color: colors.textMuted, marginLeft: 6 }}>
                                ({item.location.name})
                              </span>
                            )}
                          </Typography>
                          {onZoomToLocation && (
                            <Tooltip title="Zoom to location on map">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (item.location) {
                                    onZoomToLocation(item.location.lat, item.location.lng, 16);
                                  }
                                }}
                                sx={{
                                  p: 0.25,
                                  color: colors.primary,
                                  '&:hover': {
                                    color: colors.accent,
                                    bgcolor: alpha(colors.primary, 0.1),
                                  },
                                }}
                              >
                                <ZoomToIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                      
                      {item.sourceLink && (
                        <Button
                          size="small"
                          startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                          href={item.sourceLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ 
                            textTransform: 'uppercase',
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            letterSpacing: '0.05em',
                            color: colors.primary,
                            py: 0.25,
                          }}
                        >
                          Source Document
                        </Button>
                      )}
                    </Box>

                    {/* Attachments Section */}
                    <Box sx={{ mt: 2 }}>
                      {/* Upload and Enrich buttons */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          startIcon={uploadingNoteId === item.id ? <CircularProgress size={14} /> : <UploadIcon sx={{ fontSize: 14 }} />}
                          onClick={(e) => { e.stopPropagation(); triggerUpload(item.id); }}
                          disabled={uploadingNoteId === item.id}
                          sx={{
                            textTransform: 'uppercase',
                            fontSize: '0.6rem',
                            fontWeight: 600,
                            letterSpacing: '0.05em',
                            color: colors.primary,
                            border: `1px dashed ${colors.border}`,
                            py: 0.5,
                            px: 1,
                            '&:hover': {
                              bgcolor: alpha(colors.primary, 0.05),
                              borderColor: colors.primary,
                            },
                          }}
                        >
                          {uploadingNoteId === item.id ? 'Uploading...' : 'Attach File'}
                        </Button>
                        
                        {/* Enrich with Tavily button - opens chat */}
                        {enrichmentEnabled && (
                          <Tooltip title="Ask questions about this entry">
                            <Button
                              size="small"
                              startIcon={<EnrichIcon sx={{ fontSize: 14 }} />}
                              onClick={(e) => { e.stopPropagation(); handleOpenEnrichChat(item); }}
                              sx={{
                                textTransform: 'uppercase',
                                fontSize: '0.6rem',
                                fontWeight: 600,
                                letterSpacing: '0.05em',
                                color: enrichChatHistory.has(item.id) ? colors.success : colors.accent,
                                border: `1px dashed ${enrichChatHistory.has(item.id) ? colors.success : colors.border}`,
                                py: 0.5,
                                px: 1,
                                '&:hover': {
                                  bgcolor: alpha(colors.accent, 0.05),
                                  borderColor: colors.accent,
                                },
                              }}
                            >
                              {enrichChatHistory.has(item.id) ? 'Ask More' : 'Research'}
                            </Button>
                          </Tooltip>
                        )}
                        
                        {uploadingNoteId === item.id && (
                          <Box sx={{ flex: 1, maxWidth: 150 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={uploadProgress} 
                              sx={{ 
                                height: 4, 
                                borderRadius: 2,
                                bgcolor: colors.surface,
                                '& .MuiLinearProgress-bar': { bgcolor: colors.primary },
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                      
                      {/* Enrichment Results */}
                      {enrichmentResults.has(item.id) && (
                        <Box sx={{ 
                          mb: 2,
                          p: 1.5,
                          bgcolor: alpha(colors.accent, 0.05),
                          borderRadius: '4px',
                          border: `1px solid ${alpha(colors.accent, 0.3)}`,
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <EnrichIcon sx={{ fontSize: 14, color: colors.accent }} />
                            <Typography sx={{
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              color: colors.accent,
                              textTransform: 'uppercase',
                              letterSpacing: '0.1em',
                            }}>
                              Web Intelligence
                            </Typography>
                          </Box>
                          
                          {/* Discovered Location - Show prominently if found */}
                          {enrichmentResults.get(item.id)?.discovered_place_name && (
                            <Box sx={{ 
                              mb: 1.5, 
                              p: 1, 
                              bgcolor: alpha(colors.success, 0.1),
                              borderRadius: '4px',
                              border: `1px solid ${alpha(colors.success, 0.3)}`,
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                <Box>
                                  <Typography sx={{
                                    fontSize: '0.6rem',
                                    color: colors.success,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                    mb: 0.5,
                                  }}>
                                    Identified Location
                                  </Typography>
                                  <Typography sx={{
                                    fontSize: '0.85rem',
                                    fontWeight: 700,
                                    color: colors.text,
                                  }}>
                                    {enrichmentResults.get(item.id)?.discovered_place_name}
                                  </Typography>
                                  {(enrichmentResults.get(item.id)?.discovered_city || enrichmentResults.get(item.id)?.discovered_country) && (
                                    <Typography sx={{
                                      fontSize: '0.7rem',
                                      color: colors.textSecondary,
                                      mt: 0.25,
                                    }}>
                                      {[
                                        enrichmentResults.get(item.id)?.discovered_city,
                                        enrichmentResults.get(item.id)?.discovered_country
                                      ].filter(Boolean).join(', ')}
                                    </Typography>
                                  )}
                                  {enrichmentResults.get(item.id)?.discovered_place_type && (
                                    <Typography sx={{
                                      fontSize: '0.6rem',
                                      color: colors.textMuted,
                                      fontStyle: 'italic',
                                      mt: 0.25,
                                    }}>
                                      Type: {enrichmentResults.get(item.id)?.discovered_place_type}
                                    </Typography>
                                  )}
                                </Box>
                                
                                {/* Update Title Button */}
                                <Tooltip title="Update entry title with this location">
                                  <Button
                                    size="small"
                                    startIcon={updatingTitleId === item.id ? <CircularProgress size={12} /> : <EditIcon sx={{ fontSize: 12 }} />}
                                    onClick={(e) => { 
                                      e.stopPropagation(); 
                                      const enrichment = enrichmentResults.get(item.id);
                                      if (enrichment) handleUpdateTitle(item.id, enrichment);
                                    }}
                                    disabled={updatingTitleId === item.id}
                                    sx={{
                                      textTransform: 'uppercase',
                                      fontSize: '0.55rem',
                                      fontWeight: 600,
                                      color: colors.success,
                                      border: `1px solid ${colors.success}`,
                                      py: 0.25,
                                      px: 0.75,
                                      minWidth: 'auto',
                                      '&:hover': {
                                        bgcolor: alpha(colors.success, 0.1),
                                      },
                                    }}
                                  >
                                    {updatingTitleId === item.id ? 'Updating...' : 'Update Title'}
                                  </Button>
                                </Tooltip>
                              </Box>
                            </Box>
                          )}
                          
                          {/* Summary */}
                          {enrichmentResults.get(item.id)?.summary && (
                            <Typography sx={{
                              fontSize: '0.75rem',
                              color: colors.text,
                              mb: 1,
                              lineHeight: 1.5,
                            }}>
                              {enrichmentResults.get(item.id)?.summary}
                            </Typography>
                          )}
                          
                          {/* Sources */}
                          {enrichmentResults.get(item.id)?.sources?.length ? (
                            <Box sx={{ mt: 1 }}>
                              <Typography sx={{
                                fontSize: '0.6rem',
                                color: colors.textMuted,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                mb: 0.5,
                              }}>
                                Sources
                              </Typography>
                              {enrichmentResults.get(item.id)?.sources.slice(0, 3).map((source, idx) => (
                                <Box key={idx} sx={{ mb: 0.5 }}>
                                  <Typography
                                    component="a"
                                    href={source.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{
                                      fontSize: '0.65rem',
                                      color: colors.primary,
                                      textDecoration: 'none',
                                      '&:hover': { textDecoration: 'underline' },
                                    }}
                                  >
                                    {source.title || source.url}
                                  </Typography>
                                  {source.snippet && (
                                    <Typography sx={{
                                      fontSize: '0.6rem',
                                      color: colors.textMuted,
                                      mt: 0.25,
                                    }}>
                                      {source.snippet.slice(0, 150)}...
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          ) : null}
                        </Box>
                      )}

                      {/* Attachments list */}
                      {attachments.get(item.id)?.length ? (
                        <Box sx={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: 1,
                          p: 1,
                          bgcolor: alpha(colors.surface, 0.5),
                          borderRadius: '4px',
                          border: `1px solid ${colors.border}`,
                        }}>
                          {attachments.get(item.id)?.map(attachment => (
                            <Box
                              key={attachment.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                p: 0.5,
                                pl: 1,
                                bgcolor: colors.surface,
                                borderRadius: '4px',
                                border: `1px solid ${colors.border}`,
                                maxWidth: 200,
                              }}
                            >
                              {/* Thumbnail or icon */}
                              {attachment.is_image ? (
                                <Box
                                  component="img"
                                  src={`${pathfinderApi.getNoteAttachmentDownloadUrl(attachment.id)}`}
                                  alt={attachment.original_filename}
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    setPreviewImage(pathfinderApi.getNoteAttachmentDownloadUrl(attachment.id));
                                  }}
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    objectFit: 'cover',
                                    borderRadius: '2px',
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.8 },
                                  }}
                                />
                              ) : (
                                <FileIcon sx={{ fontSize: 20, color: colors.textMuted }} />
                              )}
                              
                              {/* File info */}
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Tooltip title={attachment.original_filename}>
                                  <Typography sx={{
                                    fontSize: '0.65rem',
                                    color: colors.text,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: 100,
                                  }}>
                                    {attachment.original_filename}
                                  </Typography>
                                </Tooltip>
                                <Typography sx={{
                                  fontSize: '0.55rem',
                                  color: colors.textMuted,
                                }}>
                                  {formatFileSize(attachment.file_size)}
                                </Typography>
                              </Box>

                              {/* Actions */}
                              <Box sx={{ display: 'flex' }}>
                                <Tooltip title="Extract insights with AI">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleProcessAttachment(attachment.id);
                                    }}
                                    disabled={processingAttachmentId === attachment.id}
                                    sx={{ p: 0.25, color: colors.accent, '&:hover': { color: colors.primary } }}
                                  >
                                    {processingAttachmentId === attachment.id ? (
                                      <CircularProgress size={12} />
                                    ) : (
                                      <AIInsightsIcon sx={{ fontSize: 14 }} />
                                    )}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download">
                                  <IconButton
                                    size="small"
                                    component="a"
                                    href={pathfinderApi.getNoteAttachmentDownloadUrl(attachment.id)}
                                    target="_blank"
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{ p: 0.25, color: colors.primary }}
                                  >
                                    <ExportIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteAttachment(attachment.id, item.id);
                                    }}
                                    sx={{ p: 0.25, color: colors.textMuted, '&:hover': { color: colors.negative } }}
                                  >
                                    <DeleteIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      ) : null}
                    </Box>
                  </Box>
                </Collapse>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        style={{ display: 'none' }}
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.json,.xml,.kml,.kmz,.gpx"
      />

      {/* Image preview dialog */}
      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        maxWidth="lg"
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={() => setPreviewImage(null)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <CloseIcon />
          </IconButton>
          {previewImage && (
            <Box
              component="img"
              src={previewImage}
              alt="Preview"
              sx={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                objectFit: 'contain',
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Note Dialog */}
      <Dialog
        open={!!editingNote}
        onClose={() => !savingEdit && setEditingNote(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.surface,
            border: `1px solid ${colors.border}`,
          },
        }}
      >
        <DialogTitle sx={{ 
          color: colors.text, 
          borderBottom: `1px solid ${colors.border}`,
          fontFamily: '"JetBrains Mono", monospace',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          fontSize: '0.9rem',
        }}>
          Edit Intel Entry
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {editingNote && (
            <Box>
              <Typography sx={{ 
                fontSize: '0.75rem', 
                color: colors.textMuted, 
                mb: 1,
                textTransform: 'uppercase',
              }}>
                {editingNote.title}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={15}
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                disabled={savingEdit}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: colors.bg,
                    color: colors.text,
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.8rem',
                    '& fieldset': { borderColor: colors.border },
                    '&:hover fieldset': { borderColor: colors.primary },
                    '&.Mui-focused fieldset': { borderColor: colors.primary },
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${colors.border}`, px: 3, py: 2 }}>
          <Button 
            onClick={() => setEditingNote(null)}
            disabled={savingEdit}
            sx={{ color: colors.textSecondary }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEdit}
            disabled={savingEdit || !editedContent.trim()}
            variant="contained"
            startIcon={savingEdit ? <CircularProgress size={16} /> : null}
            sx={{ 
              bgcolor: colors.primary,
              '&:hover': { bgcolor: colors.accent },
            }}
          >
            {savingEdit ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Confirmation Dialog */}
      <Dialog
        open={deleteAllConfirmOpen}
        onClose={() => !deletingAll && setDeleteAllConfirmOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: colors.surface,
            border: `1px solid ${colors.border}`,
          },
        }}
      >
        <DialogTitle sx={{ 
          color: colors.negative, 
          fontFamily: '"JetBrains Mono", monospace',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          fontSize: '0.9rem',
        }}>
          Confirm Delete All
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: colors.text, mb: 2 }}>
            Are you sure you want to delete <strong>{filteredIntel.length}</strong> intel {filteredIntel.length === 1 ? 'entry' : 'entries'}?
          </Typography>
          <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem' }}>
            This action cannot be undone. All selected notes, dwells, co-locations, and other entries will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${colors.border}`, px: 3, py: 2 }}>
          <Button 
            onClick={() => setDeleteAllConfirmOpen(false)}
            disabled={deletingAll}
            sx={{ color: colors.textSecondary }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteAll}
            disabled={deletingAll}
            variant="contained"
            startIcon={deletingAll ? <CircularProgress size={16} color="inherit" /> : <DeleteAllIcon />}
            sx={{ 
              bgcolor: colors.negative,
              '&:hover': { bgcolor: alpha(colors.negative, 0.8) },
            }}
          >
            {deletingAll ? 'Deleting...' : `Delete ${filteredIntel.length} Items`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog
        open={addNoteOpen}
        onClose={() => !savingNewNote && setAddNoteOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.surface,
            border: `1px solid ${colors.border}`,
          },
        }}
      >
        <DialogTitle sx={{ 
          color: colors.success, 
          fontFamily: '"JetBrains Mono", monospace',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          fontSize: '0.9rem',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}>
          <AddNoteIcon sx={{ fontSize: 20 }} />
          Add Intel Note
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {/* Target Device Selection */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Typography sx={{ 
              fontSize: '0.75rem', 
              color: colors.textMuted, 
              mb: 1,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
            }}>
              Target Device
            </Typography>
            <Select
              value={newNoteDeviceId}
              onChange={(e) => setNewNoteDeviceId(e.target.value as number)}
              displayEmpty
              size="small"
              sx={{
                bgcolor: colors.bg,
                color: colors.text,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                '& .MuiSelect-icon': { color: colors.textMuted },
              }}
            >
              <MenuItem value="" disabled>
                <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                  Select a target device...
                </Typography>
              </MenuItem>
              {availableDevices.map(device => (
                <MenuItem key={device.id} value={device.id}>
                  <Typography sx={{ fontSize: '0.85rem' }}>{device.label}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Note Content */}
          <Typography sx={{ 
            fontSize: '0.75rem', 
            color: colors.textMuted, 
            mb: 1,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
          }}>
            Note Content
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={8}
            placeholder="Enter your intelligence note here...

You can include:
- Observations and analysis
- Location details
- Entity information
- Timestamps and events"
            value={newNoteContent}
            onChange={(e) => setNewNoteContent(e.target.value)}
            disabled={savingNewNote}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: colors.bg,
                color: colors.text,
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.8rem',
                '& fieldset': { borderColor: colors.border },
                '&:hover fieldset': { borderColor: colors.primary },
                '&.Mui-focused fieldset': { borderColor: colors.primary },
              },
            }}
          />

          {/* File Upload Section */}
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ 
              fontSize: '0.75rem', 
              color: colors.textMuted, 
              mb: 1,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
            }}>
              Attach Document (Optional)
            </Typography>
            
            <input
              type="file"
              ref={newNoteFileInputRef}
              style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif,.webp"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) setNewNoteFile(file);
              }}
            />
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadIcon />}
                onClick={() => newNoteFileInputRef.current?.click()}
                disabled={savingNewNote}
                sx={{
                  color: colors.textSecondary,
                  borderColor: colors.border,
                  fontSize: '0.75rem',
                  '&:hover': {
                    borderColor: colors.primary,
                    bgcolor: alpha(colors.primary, 0.05),
                  },
                }}
              >
                Choose File
              </Button>
              
              {newNoteFile && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  px: 1.5,
                  py: 0.5,
                  bgcolor: alpha(colors.success, 0.1),
                  borderRadius: '4px',
                  border: `1px solid ${alpha(colors.success, 0.3)}`,
                }}>
                  <FileIcon sx={{ fontSize: 16, color: colors.success }} />
                  <Typography sx={{ 
                    fontSize: '0.75rem', 
                    color: colors.text,
                    maxWidth: 200,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {newNoteFile.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setNewNoteFile(null)}
                    sx={{ p: 0.25, color: colors.textMuted, '&:hover': { color: colors.negative } }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              )}
            </Box>
            
            <Typography sx={{ 
              fontSize: '0.65rem', 
              color: colors.textMuted, 
              mt: 0.5,
              fontStyle: 'italic',
            }}>
              Supported: PDF, Word, Text, Images (PNG, JPG, GIF, WebP)
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${colors.border}`, px: 3, py: 2 }}>
          <Button 
            onClick={() => { setAddNoteOpen(false); setNewNoteContent(''); setNewNoteDeviceId(''); setNewNoteFile(null); }}
            disabled={savingNewNote}
            sx={{ color: colors.textSecondary }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateNote}
            disabled={savingNewNote || (!newNoteContent.trim() && !newNoteFile) || !newNoteDeviceId}
            variant="contained"
            startIcon={savingNewNote ? <CircularProgress size={16} color="inherit" /> : <AddNoteIcon />}
            sx={{ 
              bgcolor: colors.success,
              '&:hover': { bgcolor: alpha(colors.success, 0.8) },
            }}
          >
            {savingNewNote ? 'Creating...' : 'Create Note'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enrichment Chat Dialog */}
      <Dialog
        open={!!enrichChatOpen}
        onClose={() => !enrichChatLoading && setEnrichChatOpen(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.surface,
            border: `1px solid ${colors.border}`,
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle sx={{ 
          color: colors.accent, 
          fontFamily: '"JetBrains Mono", monospace',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          fontSize: '0.85rem',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}>
          <EnrichIcon sx={{ fontSize: 18 }} />
          Research Assistant
          <IconButton 
            size="small" 
            onClick={() => setEnrichChatOpen(null)}
            sx={{ ml: 'auto', color: colors.textMuted }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {enrichChatOpen && (
            <>
              {/* Note Context */}
              <Box sx={{ 
                p: 1.5, 
                bgcolor: alpha(colors.primary, 0.05), 
                borderBottom: `1px solid ${colors.border}`,
              }}>
                <Typography sx={{ 
                  fontSize: '0.7rem', 
                  color: colors.textMuted, 
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  mb: 0.5,
                }}>
                  Context
                </Typography>
                <Typography sx={{ 
                  fontSize: '0.8rem', 
                  color: colors.text,
                  fontWeight: 600,
                }}>
                  {enrichChatOpen.title}
                </Typography>
                {enrichChatOpen.location && (
                  <Typography sx={{ fontSize: '0.7rem', color: colors.textSecondary }}>
                    {enrichChatOpen.location.name || `${enrichChatOpen.location.lat.toFixed(4)}, ${enrichChatOpen.location.lng.toFixed(4)}`}
                  </Typography>
                )}
              </Box>

              {/* Quick Questions */}
              <Box sx={{ 
                p: 1.5, 
                borderBottom: `1px solid ${colors.border}`,
                display: 'flex',
                gap: 0.5,
                flexWrap: 'wrap',
              }}>
                <Typography sx={{ 
                  fontSize: '0.65rem', 
                  color: colors.textMuted,
                  width: '100%',
                  mb: 0.5,
                }}>
                  Quick Questions:
                </Typography>
                {enrichChatOpen.location && (
                  <>
                    <Chip
                      label="What is this place?"
                      size="small"
                      onClick={() => setEnrichChatQuery('What is this place? What type of facility or location is it?')}
                      sx={{ 
                        fontSize: '0.65rem', 
                        height: 24,
                        bgcolor: alpha(colors.accent, 0.1),
                        color: colors.accent,
                        '&:hover': { bgcolor: alpha(colors.accent, 0.2) },
                      }}
                    />
                    <Chip
                      label="What's nearby?"
                      size="small"
                      onClick={() => setEnrichChatQuery('What notable locations, businesses, or landmarks are nearby?')}
                      sx={{ 
                        fontSize: '0.65rem', 
                        height: 24,
                        bgcolor: alpha(colors.accent, 0.1),
                        color: colors.accent,
                        '&:hover': { bgcolor: alpha(colors.accent, 0.2) },
                      }}
                    />
                  </>
                )}
                <Chip
                  label="Historical significance?"
                  size="small"
                  onClick={() => setEnrichChatQuery('What is the historical significance or background of this location or entity?')}
                  sx={{ 
                    fontSize: '0.65rem', 
                    height: 24,
                    bgcolor: alpha(colors.accent, 0.1),
                    color: colors.accent,
                    '&:hover': { bgcolor: alpha(colors.accent, 0.2) },
                  }}
                />
                <Chip
                  label="Who operates here?"
                  size="small"
                  onClick={() => setEnrichChatQuery('Who operates or owns this location? What organizations are associated with it?')}
                  sx={{ 
                    fontSize: '0.65rem', 
                    height: 24,
                    bgcolor: alpha(colors.accent, 0.1),
                    color: colors.accent,
                    '&:hover': { bgcolor: alpha(colors.accent, 0.2) },
                  }}
                />
                <Chip
                  label="Recent news?"
                  size="small"
                  onClick={() => setEnrichChatQuery('What recent news or events are associated with this location or entity?')}
                  sx={{ 
                    fontSize: '0.65rem', 
                    height: 24,
                    bgcolor: alpha(colors.accent, 0.1),
                    color: colors.accent,
                    '&:hover': { bgcolor: alpha(colors.accent, 0.2) },
                  }}
                />
              </Box>

              {/* Chat History */}
              <Box sx={{ 
                flex: 1, 
                overflowY: 'auto', 
                p: 1.5,
                minHeight: 200,
                maxHeight: 300,
              }}>
                {(enrichChatHistory.get(enrichChatOpen.id) || []).length === 0 ? (
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: 4, 
                    color: colors.textMuted,
                  }}>
                    <EnrichIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                    <Typography sx={{ fontSize: '0.8rem' }}>
                      Ask a question about this intel entry
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', mt: 0.5 }}>
                      The AI will search the web for relevant information
                    </Typography>
                  </Box>
                ) : (
                  (enrichChatHistory.get(enrichChatOpen.id) || []).map((msg, idx) => (
                    <Box 
                      key={idx}
                      sx={{ 
                        mb: 1.5,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: msg.type === 'user' ? 'flex-end' : 'flex-start',
                      }}
                    >
                      <Box sx={{
                        maxWidth: '85%',
                        p: 1.5,
                        borderRadius: '8px',
                        bgcolor: msg.type === 'user' 
                          ? alpha(colors.primary, 0.2) 
                          : alpha(colors.surface, 0.8),
                        border: `1px solid ${msg.type === 'user' ? colors.primary : colors.border}`,
                      }}>
                        <Typography sx={{ 
                          fontSize: '0.8rem', 
                          color: colors.text,
                          whiteSpace: 'pre-wrap',
                        }}>
                          {msg.content}
                        </Typography>
                        
                        {/* Sources */}
                        {msg.sources && msg.sources.length > 0 && (
                          <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${colors.border}` }}>
                            <Typography sx={{ 
                              fontSize: '0.6rem', 
                              color: colors.textMuted,
                              textTransform: 'uppercase',
                              letterSpacing: '0.1em',
                              mb: 0.5,
                            }}>
                              Sources
                            </Typography>
                            {msg.sources.slice(0, 3).map((source, sIdx) => (
                              <Box 
                                key={sIdx}
                                component="a"
                                href={source.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  display: 'block',
                                  fontSize: '0.7rem',
                                  color: colors.accent,
                                  textDecoration: 'none',
                                  mb: 0.25,
                                  '&:hover': { textDecoration: 'underline' },
                                }}
                              >
                                {source.title || source.url}
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ))
                )}
                
                {enrichChatLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: colors.textMuted }}>
                    <CircularProgress size={16} />
                    <Typography sx={{ fontSize: '0.75rem' }}>Searching...</Typography>
                  </Box>
                )}
              </Box>

              {/* Input */}
              <Box sx={{ 
                p: 1.5, 
                borderTop: `1px solid ${colors.border}`,
                display: 'flex',
                gap: 1,
              }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Ask a question about this entry..."
                  value={enrichChatQuery}
                  onChange={(e) => setEnrichChatQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleAskEnrichment()}
                  disabled={enrichChatLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: colors.bg,
                      color: colors.text,
                      fontSize: '0.85rem',
                      '& fieldset': { borderColor: colors.border },
                      '&:hover fieldset': { borderColor: colors.primary },
                      '&.Mui-focused fieldset': { borderColor: colors.primary },
                    },
                  }}
                />
                <Button
                  id="enrich-ask-btn"
                  variant="contained"
                  onClick={handleAskEnrichment}
                  disabled={!enrichChatQuery.trim() || enrichChatLoading}
                  sx={{
                    bgcolor: colors.primary,
                    minWidth: 80,
                    '&:hover': { bgcolor: colors.accent },
                  }}
                >
                  {enrichChatLoading ? <CircularProgress size={20} color="inherit" /> : 'Ask'}
                </Button>
              </Box>
              
              {/* Save to Note Button - appears when there's chat history */}
              {enrichChatOpen && (enrichChatHistory.get(enrichChatOpen.id) || []).length > 0 && (
                <Box sx={{ 
                  mt: 1, 
                  pt: 1, 
                  borderTop: `1px solid ${colors.border}`,
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}>
                  <Tooltip title="Append all Q&A to the note">
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleSaveEnrichmentToNote}
                      disabled={enrichChatLoading}
                      startIcon={enrichChatLoading ? <CircularProgress size={14} color="inherit" /> : <AddNoteIcon />}
                      sx={{
                        bgcolor: colors.success,
                        fontSize: '0.75rem',
                        '&:hover': { bgcolor: alpha(colors.success, 0.8) },
                      }}
                    >
                      Save Research to Note
                    </Button>
                  </Tooltip>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default IntelPacket;

